import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Bookmark, BookmarkCheck, FileText, Lock, MapPin, Phone, PlayCircle, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtMoney, fmtNum, imgSrc, downloadBlob } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSeo } from "../lib/seo";
import { PHONE, PHONE_HREF } from "../lib/constants";
import { Disclaimer, Field } from "../components/Common";
import { StatusBadge } from "../components/PropertyCard";
import { PageFade } from "../components/Motion";

const Metric = ({ label, value, tag, testid }) =>
  value ? (
    <div className="bg-white p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-mist">{label}</p>
      <p className="metric mt-2 text-xl">{value}</p>
      {tag && <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-gold">{tag}</p>}
    </div>
  ) : null;

const Block = ({ title, children }) =>
  children ? (
    <div className="border-t border-navy/10 pt-8">
      <h2 className="text-2xl font-semibold text-navy">{title}</h2>
      <div className="mt-4 text-ink/75 leading-relaxed">{children}</div>
    </div>
  ) : null;

function Gallery({ images, title }) {
  const [i, setI] = useState(0);
  return (
    <div data-testid="property-gallery">
      <div className="aspect-[16/10] bg-navy overflow-hidden">
        <img src={imgSrc(images[i])} alt={title} className="h-full w-full object-cover" />
      </div>
      {images.length > 1 && (
        <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
          {images.map((src, k) => (
            <button key={src + k} onClick={() => setI(k)} className={`aspect-[4/3] overflow-hidden border-2 ${k === i ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"}`} data-testid={`gallery-thumb-${k}`}>
              <img src={imgSrc(src)} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RequestPanel({ p, reload }) {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const status = p.request_status;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/properties/${p.id}/requests`, { ...f, wants_documents: true });
      setSent(true);
      toast.success("Request received");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (status || sent)
    return (
      <div className="bg-navy text-paper p-6" data-testid="request-status">
        <p className="eyebrow">Request Status</p>
        <p className="mt-3 font-serif text-2xl">{status || "Pending"}</p>
        <p className="mt-2 text-sm text-paper/70">Sido Holdings will review your request and follow up regarding next steps and document access.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="bg-white border border-navy/10 p-6 space-y-4" data-testid="request-info-form">
      <p className="eyebrow">Request Deal Information</p>
      <p className="text-sm text-ink/70">Request the full package, confidential details and documents.</p>
      <Field label="Name"><input required className="field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="request-name" /></Field>
      <Field label="Email"><input required type="email" className="field" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="request-email" /></Field>
      <Field label="Phone"><input type="tel" className="field" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="request-phone" /></Field>
      <Field label="Message"><textarea className="field min-h-[90px]" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="request-message" /></Field>
      <button disabled={busy} className="btn-primary w-full" data-testid="request-submit">{busy ? "Sending…" : "Request Deal Information"}</button>
      {!user && <p className="text-xs text-mist"><Link to="/login" className="underline">Sign in</Link> to track requests and unlock documents once approved.</p>}
    </form>
  );
}

function Documents({ p }) {
  const { user } = useAuth();
  const unlocked = p.access === "granted";
  return (
    <div className="border-t border-navy/10 pt-8" data-testid="property-documents">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-navy">Documents</h2>
        <span className="flex items-center gap-1.5 text-xs text-mist">{unlocked ? <Unlock className="h-3.5 w-3.5 text-emerald-700" /> : <Lock className="h-3.5 w-3.5" />}{unlocked ? "Access granted" : "Available after approval"}</span>
      </div>
      {unlocked && p.confidential_notes && (
        <div className="mt-4 bg-white border-l-2 border-gold p-5 text-sm" data-testid="confidential-notes">
          <p className="text-[10px] uppercase tracking-wider text-mist">Confidential · {p.address}</p>
          <p className="mt-2 text-ink/80">{p.confidential_notes}</p>
        </div>
      )}
      <ul className="mt-4 divide-y divide-navy/10 border border-navy/10 bg-white">
        {p.documents.length === 0 && <li className="p-4 text-sm text-mist">Offering memorandum, rent roll, T-12 and other documents are shared with approved investors.</li>}
        {p.documents.map((d) => (
          <li key={d.id} className="flex items-center justify-between p-4 text-sm">
            <span className="flex items-center gap-2 text-navy"><FileText className="h-4 w-4 text-gold" />{d.name}</span>
            {unlocked ? (
              <button onClick={() => downloadBlob(`/properties/${p.id}/documents/${d.id}`, d.name).catch((e) => toast.error(errMsg(e)))} className="text-navy font-semibold hover:text-gold" data-testid={`doc-download-${d.id}`}>Download</button>
            ) : (
              <Lock className="h-4 w-4 text-mist" />
            )}
          </li>
        ))}
      </ul>
      {!unlocked && !user && <p className="mt-3 text-xs text-mist">Sign in and request access to view sensitive property information.</p>}
    </div>
  );
}

function AskPanel({ p }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/threads", { property_id: p.id, body: text });
      setText("");
      setSent(true);
      toast.success("Message sent to Sido Holdings");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={send} className="bg-white border border-navy/10 p-6 space-y-3" data-testid="ask-panel">
      <p className="eyebrow">Ask a Question</p>
      {sent && <p className="text-sm text-navy" data-testid="ask-sent">Sent. Continue the conversation in your <Link to="/dashboard?tab=messages" className="underline">portal messages</Link>.</p>}
      <textarea required className="field min-h-[90px] text-sm" placeholder="Ask about this property…" value={text} onChange={(e) => setText(e.target.value)} data-testid="ask-input" />
      <button disabled={busy} className="btn-ghost w-full text-navy hover:bg-navy/5" data-testid="ask-send">{busy ? "Sending…" : "Send Message"}</button>
    </form>
  );
}

export default function PropertyDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const load = () => api.get(`/properties/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  useEffect(() => { if (user !== null) load(); }, [slug, user]); // eslint-disable-line
  useSeo(p ? `${p.title} — ${p.city}, Oregon` : "Opportunity", p?.summary);

  if (err) return <div className="pt-40 pb-32 container-x text-navy" data-testid="property-not-found">Opportunity not found. <Link to="/opportunities" className="underline">Back to opportunities</Link></div>;
  if (!p) return <div className="pt-40 pb-32 container-x text-mist">Loading…</div>;
  const L = p.metric_labels || {};

  const toggleSave = async () => {
    try {
      if (p.saved) await api.delete(`/me/saved/${p.id}`); else await api.post(`/me/saved/${p.id}`);
      setP({ ...p, saved: !p.saved });
      toast.success(p.saved ? "Removed from saved" : "Saved to your portal");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <PageFade>
      <section className="bg-paper pt-[100px] sm:pt-[110px] pb-20" data-testid="property-detail">
        <div className="container-x">
          <Link to={p.category === "distressed" ? "/distressed" : "/opportunities"} className="inline-flex items-center gap-2 text-sm text-mist hover:text-navy" data-testid="property-back"><ArrowLeft className="h-4 w-4" /> All opportunities</Link>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <StatusBadge status={p.status} testid="property-status" />
            {p.category === "distressed" && <span className="bg-navy text-paper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]">{p.distress_type}</span>}
            {p.is_sample && <span className="border border-navy/30 px-2 py-0.5 font-mono text-[10px] tracking-widest text-navy" data-testid="detail-sample-badge">SAMPLE LISTING — FOR ILLUSTRATION ONLY</span>}
            {p.match && <span className="bg-gold text-navy px-2.5 py-0.5 font-mono text-xs font-semibold" data-testid="detail-match-score">{p.match.score}% Match</span>}
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-medium text-navy leading-[1.05]" data-testid="property-title">{p.title}</h1>
          <p className="mt-3 flex items-center gap-2 text-mist"><MapPin className="h-4 w-4" />{p.city}, {p.county ? `${p.county} County, ` : ""}Oregon · {p.property_type}</p>
          {p.match && <p className="mt-2 text-sm text-navy/70">{p.match.summary}</p>}

          <div className="mt-10 grid gap-10 lg:grid-cols-[1.7fr_1fr]">
            <div className="space-y-10 min-w-0">
              <Gallery images={p.images?.length ? p.images : []} title={p.title} />
              {p.video_url && <a href={p.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-navy font-semibold" data-testid="property-video-link"><PlayCircle className="h-5 w-5 text-gold" /> Watch property video</a>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-navy/10 border border-navy/10" data-testid="property-metrics">
                <Metric label="Asking Price" value={fmtMoney(p.asking_price)} tag={L.asking_price} testid="metric-asking-price" />
                <Metric label="Est. Acquisition Price" value={fmtMoney(p.est_acquisition_price)} tag={L.est_acquisition_price || "Estimated"} testid="metric-est-price" />
                <Metric label="Building Size" value={fmtNum(p.sqft, " SF")} tag={L.sqft} />
                <Metric label="Lot Size" value={p.lot_size} />
                <Metric label="Units" value={fmtNum(p.units)} />
                <Metric label="Occupancy" value={p.occupancy != null ? `${p.occupancy}%` : null} tag={L.occupancy} />
                <Metric label="NOI" value={fmtMoney(p.noi)} tag={L.noi} testid="metric-noi" />
                <Metric label="Cap Rate" value={p.cap_rate ? `${p.cap_rate}%` : null} tag={L.cap_rate} testid="metric-cap-rate" />
                <Metric label="Strategy" value={(p.strategies || []).join(", ")} />
              </div>
              <Block title="Property Overview">{p.summary}</Block>
              <Block title="Rent Information">{p.rent_info}</Block>
              <Block title="Operating Expenses">{p.operating_expenses}</Block>
              <Block title="Renovation / Value-Add">{p.value_add}</Block>
              <Block title="Investment Thesis">{p.investment_thesis && <><p>{p.investment_thesis}</p><p className="mt-3 text-xs text-mist">Thesis reflects Sido Holdings's preliminary view and is not a guarantee of any outcome.</p></>}</Block>
              <Block title="Key Considerations">{p.key_considerations?.length > 0 && <ul className="space-y-2">{p.key_considerations.map((k) => <li key={k} className="flex gap-3"><span className="mt-2.5 h-px w-4 bg-gold shrink-0" />{k}</li>)}</ul>}</Block>
              <Block title="Due Diligence Status">{p.due_diligence_status}</Block>
              <Documents p={p} />
              <Disclaimer testid="property-disclaimer">This property summary is for informational purposes only. Information may be provided by the seller or other third parties and has not been independently verified by Sido Holdings. All figures labeled Projected, Estimated or Provided by Seller are not guarantees of future performance. Investors must perform independent due diligence. Availability and terms are subject to change.</Disclaimer>
            </div>
            <aside className="space-y-4 lg:sticky lg:top-24 self-start">
              {user?.role === "investor" && (
                <button onClick={toggleSave} className="btn-ghost w-full text-navy hover:bg-navy/5" data-testid="property-save-btn">
                  {p.saved ? <BookmarkCheck className="h-4 w-4 text-gold" /> : <Bookmark className="h-4 w-4" />}{p.saved ? "Saved" : "Save Property"}
                </button>
              )}
              <RequestPanel p={p} reload={load} />
              {user?.role === "investor" && <AskPanel p={p} />}
              <a href={PHONE_HREF} className="flex items-center justify-center gap-2 border border-navy/15 bg-white p-4 text-sm text-navy hover:border-navy/40" data-testid="property-call-link"><Phone className="h-4 w-4 text-gold" /> Call Abdirahman · {PHONE}</a>
            </aside>
          </div>
        </div>
      </section>
    </PageFade>
  );
}
